import React, { useState } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import shallow from 'zustand/shallow';
import { NotebookViews, TaskCategories } from '../../../../interfaces/notebook';
import { useUpdateTasksAsRead } from '../../../../queries/Notebook';
import useNotebookStore from '../../../../stores/notebookStore';
import { NOTEBOOK_EVENTS } from '../../../../Utils/analytics/constants';
import { trackNotebookActionEvent } from '../../../../Utils/analytics/notebook';
import { getNotebookAnalyticsProps } from '../../../../Utils/notebook';
import { NotebookStore } from '../../../../stores/notebookStore/types';
import useNotebookPusher from '../useNotebookPusher';
import { useProfileInfoFetchQuery } from '../../../../queries/Profile';
import { getCurrentDateForTimeZone } from '../../../../Utils/date';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  GET_NOTEBOOK_BOARD_SECTIONS,
  GET_NOTEBOOK_BOARD_TASKS,
  GET_NOTEBOOK_UNIFIED_SECTION,
} from '../../../../constants/endpoints';
import { useNotebookTransactionsController } from '../../../../hooks/useNotebookTransactionsController';
import { notebookViewData } from './data';

const notebookPageSelector = (store: NotebookStore) => ({
  notes: store.deadlineNotes,
  dragAndDropTask: store.dragAndDropTask,
  currentTaskData: store.currentTaskData,
  erroredTaskIDs: store.erroredTaskIDs,
  resetNotes: store.resetNotes,
  currentView: store.currentView,
  toggleCurrentView: store.toggleCurrentView,
  resetNotebookStoreData: store.resetNotebookStoreData,
});

const useNotebookViewController = () => {
  const {
    notes,
    dragAndDropTask,
    erroredTaskIDs,
    currentView,
    toggleCurrentView,
    resetNotebookStoreData,
  } = useNotebookStore(notebookPageSelector, shallow);
  const [hideNotebookNavigationModal, setNotebookNavigationModal] =
    useState(true);

  const [shouldShowModalForCurrentUser, setShouldShowModalForCurrentUser] =
    useState(true);

  const queryClient = useQueryClient();

  const handleViewChange = useCallback(
    (event: React.ChangeEvent<unknown>, newValue: string) => {
      const updatedView = newValue as NotebookViews;
      toggleCurrentView(updatedView);
    },
    [toggleCurrentView],
  );

  useEffect(() => {
    switch (currentView) {
      case NotebookViews.Board:
        queryClient.invalidateQueries(GET_NOTEBOOK_BOARD_SECTIONS);
        queryClient.invalidateQueries([GET_NOTEBOOK_BOARD_TASKS]);
        break;

      case NotebookViews.Deadline:
        queryClient.invalidateQueries(GET_NOTEBOOK_UNIFIED_SECTION);

        break;

      default:
        break;
    }
  }, [currentView, queryClient]);

  useNotebookPusher();

  const { data: profileInfo } = useProfileInfoFetchQuery();
  const currentUserTimezone = useMemo(
    () => profileInfo?.member.timeZone || '',
    [profileInfo?.member.timeZone],
  );
  const currentUserId = useMemo(
    () => profileInfo?.member.memberId,
    [profileInfo?.member.memberId],
  );
  const { mutate: markTasksAsRead } = useUpdateTasksAsRead();
  const currentLocation = useLocation();

  useEffect(() => {
    setNotebookNavigationModal(false);
    const userInfo = localStorage.getItem('notebook');
    if (userInfo) {
      const parsedUserInfo = JSON.parse(userInfo);
      setShouldShowModalForCurrentUser(
        !!parsedUserInfo[`${currentUserId}`]?.showModal,
      );
    }
    const currentViewName = currentLocation.pathname.includes(
      NotebookViews.Board,
    )
      ? NotebookViews.Board
      : NotebookViews.Deadline;
    toggleCurrentView(currentViewName);
    return () => {
      resetNotebookStoreData();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabsValue = useMemo(() => {
    switch (currentView) {
      case NotebookViews.Board:
        return 0;
      case NotebookViews.Deadline:
        return 1;
      default:
        return 0;
    }
  }, [currentView]);

  useNotebookTransactionsController();

  const onDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) {
      return;
    }
    const originalCategory = source.droppableId as TaskCategories;
    const updatedCategory = destination.droppableId as TaskCategories;
    dragAndDropTask(
      result.draggableId,
      {
        index: source.index,
        category: originalCategory,
      },
      {
        index: destination.index,
        category: updatedCategory,
      },
      currentUserTimezone,
    );
    const notebookAnalyticsProps = getNotebookAnalyticsProps(
      originalCategory,
      updatedCategory,
    );
    trackNotebookActionEvent({
      action: NOTEBOOK_EVENTS.NOTEBOOK_SECTION_CHANGED,
      ...notebookAnalyticsProps,
    });
  };

  const handlePersistingModalFlag = useCallback(() => {
    setShouldShowModalForCurrentUser(false);
    localStorage.setItem(
      'notebook',
      JSON.stringify({
        [`${currentUserId}`]: {
          showModal: false,
          timestamp: getCurrentDateForTimeZone(currentUserTimezone),
        },
      }),
    );
  }, [currentUserId, currentUserTimezone]);

  return {
    notes,
    currentView,
    onDragEnd,
    handleViewChange,
    markTasksAsRead,
    hideNotebookNavigationModal,
    handleDisableModalPrompts: handlePersistingModalFlag,
    shouldShowModalForCurrentUser,
    erroredTaskIDs,
    tabsValue,
    notebookTabs: notebookViewData,
  };
};

export default useNotebookViewController;
